/**
 * Below-the-fold operations + labor bundle for a single `:Line` (`$lineId`).
 * Kept separate from `PLANT_LINE_DETAIL_CYPHER` so the header query stays
 * bounded and the Operation / Crew panels can load independently.
 *
 * Returns one row:
 *   - `operations` — LineOperation bundles owned via `HAS_LINE_OPERATION`,
 *     each with labor/equipment participation, op-level schedules /
 *     triggers / procedures, LineOperation↔LineOperation FEEDS ids, and
 *     exception ops anchored via inbound `RECOVERS` / `FALLBACK_FOR`
 *   - `crewRequirements` — shared line capacity (unchanged)
 *   - `stations` / `flowStations` / `flowEdges` / `flowNeighbors` —
 *     legacy HumanLabor path for graphs not yet migrated to LineOperation
 *
 * Collection sizes are capped inside each `CALL` before `collect`
 * (operations/stations/equipment ≤ 1000, crew ≤ 100, flow edges ≤ 5000).
 * The trailing `LIMIT 10000` is the per-id detail-query convention: a
 * ceiling far above what this single aggregated row can reach.
 */
export const PLANT_LINE_OPERATIONS_CYPHER = `
  MATCH (l:Line {id: $lineId})

  CALL {
    WITH l
    OPTIONAL MATCH (l)-[:HAS_LINE_OPERATION]->(op:LineOperation)
    WITH l, op
    ORDER BY coalesce(op.sequence_number, 1000000000), toLower(coalesce(op.name, op.id))
    LIMIT 1000
    OPTIONAL MATCH (op)-[rl:REQUIRES_LABOR]->(hl:HumanLabor)
    WITH l, op, rl, hl
    OPTIONAL MATCH (hl)-[sb:STAFFED_BY]->(role:Role)
    WITH l, op, rl, hl,
         [row IN collect(
           CASE WHEN role IS NULL THEN null
           ELSE { role: role, props: properties(sb) }
           END
         ) WHERE row IS NOT NULL] AS staffing
    OPTIONAL MATCH (hl)-[:SCHEDULED_BY]->(spHl:ScheduleProfile)
    WITH l, op, rl, hl, staffing,
         [n IN collect(DISTINCT spHl) WHERE n IS NOT NULL] AS hlSchedules
    OPTIONAL MATCH (hl)-[tbHl:TRIGGERED_BY]->(tagHl:Tag)
    WITH l, op, rl, hl, staffing, hlSchedules,
         [row IN collect(
           CASE WHEN tagHl IS NULL THEN null
           ELSE { tag: tagHl, props: properties(tbHl) }
           END
         ) WHERE row IS NOT NULL] AS hlTriggerRows
    WITH l, op, rl, hl, staffing, hlSchedules,
         CASE WHEN size(hlTriggerRows) = 0 THEN null ELSE head(hlTriggerRows) END AS hlTrigger
    OPTIONAL MATCH (procHl:Procedure)-[:PROCEDURE_FOR]->(hl)
    WITH l, op, rl, hl, staffing, hlSchedules, hlTrigger,
         [n IN collect(DISTINCT procHl) WHERE n IS NOT NULL] AS hlProcedures
    WITH l, op,
         [row IN collect(
           CASE WHEN hl IS NULL THEN null
           ELSE {
             task: hl,
             edge: {
               type: type(rl),
               source: op.id,
               target: hl.id,
               props: properties(rl)
             },
             staffing: staffing,
             schedules: hlSchedules,
             trigger: hlTrigger,
             procedures: hlProcedures
           }
           END
         ) WHERE row IS NOT NULL] AS labor
    OPTIONAL MATCH (op)-[ue:USES_EQUIPMENT]->(eq:Equipment)
    WITH l, op, labor,
         [row IN collect(
           CASE WHEN eq IS NULL THEN null
           ELSE { equipment: eq, usageKind: ue.usage_kind }
           END
         ) WHERE row IS NOT NULL] AS equipment
    OPTIONAL MATCH (procOp:Procedure)-[:PROCEDURE_FOR]->(op)
    WITH l, op, labor, equipment,
         [n IN collect(DISTINCT procOp) WHERE n IS NOT NULL] AS procedures
    OPTIONAL MATCH (op)-[:SCHEDULED_BY]->(spOp:ScheduleProfile)
    WITH l, op, labor, equipment, procedures,
         [n IN collect(DISTINCT spOp) WHERE n IS NOT NULL] AS schedules
    OPTIONAL MATCH (op)-[tbOp:TRIGGERED_BY]->(tagOp:Tag)
    WITH l, op, labor, equipment, procedures, schedules,
         [row IN collect(
           CASE WHEN tagOp IS NULL THEN null
           ELSE { tag: tagOp, props: properties(tbOp) }
           END
         ) WHERE row IS NOT NULL] AS triggerRows
    WITH l, op, labor, equipment, procedures, schedules,
         CASE WHEN size(triggerRows) = 0 THEN null ELSE head(triggerRows) END AS trigger
    OPTIONAL MATCH (prev:LineOperation)-[:FEEDS]->(op)
    WITH l, op, labor, equipment, procedures, schedules, trigger,
         [id IN collect(DISTINCT prev.id) WHERE id IS NOT NULL] AS feedsIn
    OPTIONAL MATCH (op)-[:FEEDS]->(next:LineOperation)
    WITH l, op, labor, equipment, procedures, schedules, trigger, feedsIn,
         [id IN collect(DISTINCT next.id) WHERE id IS NOT NULL] AS feedsOut
    OPTIONAL MATCH (recoverOp:LineOperation)-[:RECOVERS]->(op)
    WITH l, op, labor, equipment, procedures, schedules, trigger, feedsIn, feedsOut,
         [n IN collect(DISTINCT recoverOp) WHERE n IS NOT NULL] AS recoveredBy
    OPTIONAL MATCH (fallbackOp:LineOperation)-[:FALLBACK_FOR]->(op)
    WITH l, op, labor, equipment, procedures, schedules, trigger, feedsIn, feedsOut, recoveredBy,
         [n IN collect(DISTINCT fallbackOp) WHERE n IS NOT NULL] AS fallbacks
    OPTIONAL MATCH (op)-[:RECOVERS|FALLBACK_FOR]->(excTarget:LineOperation)
    WITH l, op, labor, equipment, procedures, schedules, trigger, feedsIn, feedsOut,
         recoveredBy, fallbacks,
         count(excTarget) > 0 AS isException
    WITH collect(
      CASE WHEN op IS NULL THEN null
      ELSE {
        operation: op,
        labor: labor,
        equipment: equipment,
        procedures: procedures,
        schedules: schedules,
        trigger: trigger,
        feedsIn: feedsIn,
        feedsOut: feedsOut,
        recoveredBy: recoveredBy,
        fallbacks: fallbacks,
        isException: isException
      }
      END
    ) AS operationRows
    RETURN [r IN operationRows WHERE r IS NOT NULL] AS operations
  }

  CALL {
    WITH l
    OPTIONAL MATCH (l)-[rl:REQUIRES_LABOR]->(hl:HumanLabor)
    WITH l, rl, hl
    ORDER BY toLower(coalesce(hl.name, hl.id))
    LIMIT 1000
    OPTIONAL MATCH (hl)-[sb:STAFFED_BY]->(role:Role)
    WITH l, rl, hl,
         [row IN collect(
           CASE WHEN role IS NULL THEN null
           ELSE { role: role, props: properties(sb) }
           END
         ) WHERE row IS NOT NULL] AS staffing
    OPTIONAL MATCH (hl)-[:SCHEDULED_BY]->(sp:ScheduleProfile)
    WITH l, rl, hl, staffing,
         [n IN collect(DISTINCT sp) WHERE n IS NOT NULL] AS schedules
    OPTIONAL MATCH (hl)-[tb:TRIGGERED_BY]->(tag:Tag)
    WITH l, rl, hl, staffing, schedules,
         [row IN collect(
           CASE WHEN tag IS NULL THEN null
           ELSE { tag: tag, props: properties(tb) }
           END
         ) WHERE row IS NOT NULL] AS triggerRows
    WITH l, rl, hl, staffing, schedules,
         CASE WHEN size(triggerRows) = 0 THEN null ELSE head(triggerRows) END AS trigger
    OPTIONAL MATCH (proc:Procedure)-[:PROCEDURE_FOR]->(hl)
    WITH l, rl, hl, staffing, schedules, trigger,
         [n IN collect(DISTINCT proc) WHERE n IS NOT NULL] AS procedures
    OPTIONAL MATCH (hl)-[:USES_EQUIPMENT]->(ueq:Equipment)
    WITH l, rl, hl, staffing, schedules, trigger, procedures,
         [n IN collect(DISTINCT ueq) WHERE n IS NOT NULL] AS usedEquipment
    WITH collect(
      CASE WHEN hl IS NULL THEN null
      ELSE {
        task: hl,
        edge: {
          type: type(rl),
          source: l.id,
          target: hl.id,
          props: properties(rl)
        },
        staffing: staffing,
        schedules: schedules,
        trigger: trigger,
        procedures: procedures,
        usedEquipment: usedEquipment
      }
      END
    ) AS stationRows
    RETURN [r IN stationRows WHERE r IS NOT NULL] AS stations
  }

  CALL {
    WITH l
    OPTIONAL MATCH (l)-[:HAS_CREW_REQUIREMENT]->(crew:CrewRequirement)
    WITH l, crew
    ORDER BY toLower(coalesce(crew.name, crew.id))
    LIMIT 100
    OPTIONAL MATCH (crew)-[sb:STAFFED_BY]->(role:Role)
    WITH l, crew,
         [row IN collect(
           CASE WHEN role IS NULL THEN null
           ELSE { role: role, props: properties(sb) }
           END
         ) WHERE row IS NOT NULL] AS staffing
    OPTIONAL MATCH (crew)-[:COVERS]->(covered:HumanLabor)
    WITH crew, staffing,
         [n IN collect(DISTINCT covered) WHERE n IS NOT NULL] AS coveredOperations
    WITH collect(
      CASE WHEN crew IS NULL THEN null
      ELSE {
        crew: crew,
        staffing: staffing,
        coveredOperations: coveredOperations
      }
      END
    ) AS crewRows
    RETURN [r IN crewRows WHERE r IS NOT NULL] AS crewRequirements
  }

  CALL {
    WITH l
    OPTIONAL MATCH (l)-[:HAS_EQUIPMENT]->(eq:Equipment)
    WITH l, eq
    ORDER BY toLower(coalesce(eq.name, eq.id))
    LIMIT 1000
    WITH l, [n IN collect(DISTINCT eq) WHERE n IS NOT NULL] AS equipment
    OPTIONAL MATCH (l)-[:REQUIRES_LABOR]->(hl:HumanLabor)
    WITH l, equipment, hl
    ORDER BY toLower(coalesce(hl.name, hl.id))
    LIMIT 1000
    WITH equipment, [n IN collect(DISTINCT hl) WHERE n IS NOT NULL] AS labor
    WITH equipment, labor, equipment + labor AS flowNodes
    OPTIONAL MATCH (a)-[f:FEEDS]->(b)
      WHERE (a IN flowNodes OR b IN flowNodes)
        AND (
          (a:HumanLabor AND b:HumanLabor) OR
          (a:HumanLabor AND b:Equipment) OR
          (a:Equipment AND b:HumanLabor)
        )
    WITH labor, flowNodes, a, f, b
    LIMIT 5000
    WITH labor, flowNodes,
         [e IN collect(DISTINCT {
           type: type(f),
           source: a.id,
           target: b.id,
           props: properties(f)
         }) WHERE e.source IS NOT NULL AND e.target IS NOT NULL] AS flowEdges,
         collect(DISTINCT a) + collect(DISTINCT b) AS edgeNodes
    RETURN labor AS flowStations,
           flowEdges,
           [n IN edgeNodes
             WHERE n IS NOT NULL AND n:Equipment AND NOT n IN flowNodes] AS flowNeighbors
  }

  RETURN operations, stations, crewRequirements, flowStations, flowEdges, flowNeighbors
  LIMIT 10000
`
